
import { isActiveElementAnInputField } from 'bits/dom'
import { trackEvent } from 'bits/pepin'
import { Component, namespace, Vue, Watch } from 'bits/vue'
import OzDivider, { OzDividerColorScheme } from 'library/v4/components/OzDivider.vue'
import OzIconButton, { OzIconButtonColorScheme, OzIconButtonSizePreset } from 'library/v4/components/OzIconButton.vue'
import tinykeys from 'tinykeys'
import type { Post } from 'types'

@Component({
  components: { OzDivider, OzIconButton },
})
export default class SurfacePanelPostExpandedToolbar extends Vue {
  @namespace('browser').Getter isSmallerThanDesktop!: boolean
  @namespace('browser').Getter isSmallerThanTabletPortrait!: boolean
  @namespace('postAction').Getter expandedPost?: Post
  @namespace('postAction').Getter expandedPostPostsToNavigate!: Post[]
  @namespace('browser').State darkMode!: true | false | 'auto'
  @namespace('postAction').Getter xExpandedPostTextPanel!: boolean

  OzDividerColorScheme = OzDividerColorScheme
  OzIconButtonColorScheme = OzIconButtonColorScheme
  OzIconButtonSizePreset = OzIconButtonSizePreset

  $refs!: {
    actionMenuButton: OzIconButton
    previousButton: OzIconButton
    nextButton: OzIconButton
  }

  get accessiblePostCounterDescription(): string {
    return this.__('Post %{postIndex} of %{numTotalPosts}', {
      postIndex: this.postIndex + 1,
      numTotalPosts: this.totalPosts,
    })
  }

  get postIndex(): number {
    return this.expandedPostPostsToNavigate.findIndex((p) => p.cid === this.expandedPost?.cid)
  }

  get totalPosts(): number {
    return this.expandedPostPostsToNavigate.length
  }

  get isFirstPost(): boolean {
    return this.postIndex === 0
  }

  get isLastPost(): boolean {
    return this.postIndex === this.totalPosts - 1
  }

  goToPrevious(): void {
    if (this.isFirstPost) return
    const post = this.expandedPostPostsToNavigate[this.postIndex - 1]
    if (post) this.expandPost({ postCid: post.cid })
  }

  goToNext(): void {
    if (this.isLastPost) return
    const post = this.expandedPostPostsToNavigate[this.postIndex + 1]
    if (post) this.expandPost({ postCid: post.cid })
  }

  @Watch('isLastPost')
  focusPreviousButton(): void {
    if (this.isLastPost) {
      this.$refs.previousButton.$el.focus()
    }
  }

  @Watch('isFirstPost')
  focusNextButton(): void {
    if (this.isFirstPost) {
      this.$refs.nextButton.$el.focus()
    }
  }

  toggleTextPanel(): void {
    this.$emit('toggle-text-panel')
  }

  togglePostActionMenu(): void {
    this.$emit('toggle-post-action-menu')
  }

  expandPost(payload): void {
    this.$emit('expand-post', payload)
  }

  close(): void {
    this.$emit('close')
  }

  removeKeyboardShortcutsListener = (): void => {}

  installKeyboardShortcuts(): void {
    this.removeKeyboardShortcutsListener = tinykeys(window, {
      ArrowLeft: () => {
        if (isActiveElementAnInputField()) return
        this.goToPrevious()
        this.$refs.previousButton?.$el?.focus()
        trackEvent('keyboard', { target: 'expanded-view-go-to-previous-post' })
      },
      ArrowRight: () => {
        if (isActiveElementAnInputField()) return
        this.goToNext()
        this.$refs.nextButton?.$el?.focus()
        trackEvent('keyboard', { target: 'expanded-view-go-to-next-post' })
      },
    })
  }

  mounted(): void {
    this.installKeyboardShortcuts()
  }

  beforeDestroy(): void {
    this.removeKeyboardShortcutsListener()
  }
}
